import { generatePublicId } from 'flow/store/engine/utils';
import { ActionType } from './constants';
import type { ElementType, PublicId } from './types';
import { elementTypes } from './types';

/**
 * The functionality in this file is duplicated from regenerateIds.ts in Typhon.
 * We consider the implementation within Typhon as the official source of truth,
 * and since we do not have a cross service library available to us at this moment, we
 * duplicate it here.
 *
 * The functionality here is already tested in Typhon.
 */

const HASH_LENGTH = 20;
const HASH_CHARACTERS = '[a-z0-9]';

/** Reads and returns the action type from a public id */
export function getActionType(publicId: PublicId): ActionType | null {
  const splitId = publicId.split('__');

  if (splitId.length > 1 && Object.values(ActionType).includes(splitId[1])) {
    return splitId[1] as ActionType;
  }

  return null;
}

/** Runs the supplied primitive handler on all non-null primitive values within the supplied data */
export function applyProcessorToPrimitives(
  data: unknown, primitiveProcessor: (arg: unknown) => void,
) {
  if (data === null) return;

  if (Array.isArray(data)) {
    data.forEach(value => applyProcessorToPrimitives(value, primitiveProcessor));
    return;
  }

  if (typeof data === 'object') {
    Object.values(data).forEach(value => applyProcessorToPrimitives(value, primitiveProcessor));
    return;
  }

  primitiveProcessor(data);
}

function getParentKey({ key, value, parentKey }: {
  key: string;
  value: unknown;
  parentKey?: string;
}): string {
  // When recursing into an object, pass the current key as parent.
  // For primitives, pass the key that contains the value (for preserveValuesUnderKeyFn).
  // At the root level, parentKey is undefined, so we use the current key.
  return value !== null && typeof value === 'object' ? key : parentKey ?? key;
}

/**
 * Returns a new copy of the data where all ids have been translated using idMap.
 * When preserveValuesUnderKey(key) returns true, string values under that key are left
 * unchanged (keys are still remapped). parentKey is internal—the key containing the
 * current value, passed through recursion for preserveValuesUnderKey.
 */
export function remapIds<T>({
  data, idMap, preserveValuesUnderKeyFn, parentKey,
}: {
  data: T;
  idMap: Record<string, string>;
  preserveValuesUnderKeyFn?: PreserveValuesUnderKeyFn;
  parentKey?: string;
},
): T | null {
  if (data === null) return null;

  if (Array.isArray(data)) {
    return data.map(value => remapIds({
      data: value, idMap, preserveValuesUnderKeyFn, parentKey,
    })) as T;
  }

  if (typeof data === 'object') {
    const remappedObject = {};

    Object.entries(data).forEach(([key, value]) => {
      const remappedKey = idMap[key] ?? key;
      remappedObject[remappedKey] = remapIds({
        data: value,
        idMap,
        preserveValuesUnderKeyFn,
        parentKey: getParentKey({ key, value, parentKey }),
      });
    });

    return remappedObject as T;
  }

  // try to replace, or preserve if id is not found in map
  if (typeof data === 'string') {
    if (preserveValuesUnderKeyFn?.(parentKey)) {
      return data as T;
    }
    return (idMap[data] ?? data) as T;
  }

  return data;
}

const escapeRegex = (s: string) => s.replaceAll(/[$()*+.?[\\\]^{|}]/g, '\\$&');

const ACTION_TYPES = Object.values(ActionType).map(escapeRegex).join('|');
const DATA_ELEMENT = Object.values(elementTypes).map(escapeRegex).join('|');

/**
 * Regex that matches IDs from generatePublicId
 * It accepts:
 *   - action__<ActionType>__<20-char hash>
 *   - <NonActionAllowedElementType>__<20-char hash>
 */
const PUBLIC_ID_REGEX = new RegExp(
  `^(?:action__(?:${ACTION_TYPES})|(?:${DATA_ELEMENT}))__(${HASH_CHARACTERS}{${HASH_LENGTH}})$`,
);

/** Returns true if the string is the same shape as a public id */
export function isPublicId(value: unknown): value is PublicId {
  return typeof value === 'string' && PUBLIC_ID_REGEX.test(value);
}


// returns a map with keys and values swapped
export function invertPublicIdMap(
  publicIdMap: Record<PublicId, PublicId>,
): Record<PublicId, PublicId> {
  return Object.fromEntries(
    Object.entries(publicIdMap).map(([key, value]) => [value, key]),
  );
}

/** Reads and returns the element type from a public id. */
export function getElementType(publicId: PublicId): ElementType | null {
  const splitId = publicId.split('__');
  if (splitId.length > 0) {
    const elementType = splitId[0];

    if (elementTypes[elementType]) {
      return elementType as ElementType;
    }
  }

  return null;
}

/** Generates a map of old to new public ids with randomized hashes for all ids within the supplied data. */
export function generateRandomizedIdPublicIdMap<T>(data: T) {
  const idMap: Record<string, string> = {};

  function mapRandomizedPublicId(dataEntry: unknown) {
    if (isPublicId(dataEntry)) {
      // do not regenerate a public id more than once
      if (idMap[dataEntry]) {
        return;
      }

      const elementType = getElementType(dataEntry);
      if (elementType === 'action') {
        idMap[dataEntry] = generatePublicId('action', getActionType(dataEntry));
        return;
      }

      idMap[dataEntry] = generatePublicId(elementType);
    }
  }

  applyProcessorToPrimitives(data, mapRandomizedPublicId);

  return idMap;
}

/**
 * Returns a copy of the data where all public ids from the supplied data have been re-mapped to new ids.
 * Remaps both keys and values. When preserveValuesUnderKey is provided, skips remapping for string
 * values under keys where it returns true.
 */
export function regeneratePublicIds<T>(
  data: T,
  preserveValuesUnderKeyFn?: PreserveValuesUnderKeyFn,
): T {
  const idMap = generateRandomizedIdPublicIdMap(data);
  return remapIds({ data, idMap, preserveValuesUnderKeyFn });
}

/** ------- Utility functions for PreserveValuesUnderKey ------- */

/**
 * Returns true if string values under this key should not be remapped.
 * Receives the key of the object that directly contains the value (undefined at root).
 * Keys under that key are still remapped, but string values under those keys are not.
 */
export type PreserveValuesUnderKeyFn = (key: string | undefined) => boolean;

/** Do not remap string values under containerContext.snippetRemoteActionMap */
export function preserveSnippetRemoteActionMap(key: string | undefined): boolean {
  return key === 'snippetRemoteActionMap';
}
